import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Token storage. Uses localStorage as the primary so the session survives
// mobile browser tab eviction — Chrome on Android (and iOS Safari to a
// lesser extent) wipe sessionStorage when a backgrounded tab is killed for
// memory, which happens routinely when a driver opens Google Maps mid-ride.
// We still read from sessionStorage as a fallback for any legacy tab that
// was logged in before the migration.
const TOKEN_KEY = "token";

// iOS Safari throws SecurityError on localStorage when the user is in
// Private Browsing, "Block All Cookies", or has content blockers that
// disable site storage. Reads can throw too (not just writes) on some
// iOS versions. Wrap every access so the app can still boot — auth
// just degrades to a "logged out" state instead of a blank screen.
const safeStorageGet = (storage, key) => {
  try { return storage.getItem(key); } catch { return null; }
};
const safeStorageSet = (storage, key, val) => {
  try { storage.setItem(key, val); } catch { /* iOS Safari Private — ignore */ }
};
const safeStorageRemove = (storage, key) => {
  try { storage.removeItem(key); } catch { /* iOS Safari Private — ignore */ }
};

export const tokenStore = {
  get() {
    return safeStorageGet(localStorage, TOKEN_KEY) || safeStorageGet(sessionStorage, TOKEN_KEY);
  },
  set(token) {
    safeStorageSet(localStorage, TOKEN_KEY, token);
    // Clear any legacy sessionStorage value so it can't override later.
    safeStorageRemove(sessionStorage, TOKEN_KEY);
  },
  clear() {
    safeStorageRemove(localStorage, TOKEN_KEY);
    safeStorageRemove(sessionStorage, TOKEN_KEY);
    // Defensive identity purge — wipe any per-driver session keys that
    // could otherwise leak from one driver's tab to another (same browser,
    // same machine, e.g. shift handover on a shared kiosk). User-level
    // preferences like driver_nav_app are kept intentionally — they're
    // configuration, not identity.
    try {
      sessionStorage.removeItem("driver_trip_view");
      sessionStorage.removeItem("driver_offline_notice_shown");
    } catch { /* sessionStorage disabled — ignore */ }
  },
};

export const api = axios.create({
  baseURL: API_BASE,
  // Global safety net so a stalled request can never hang the UI (e.g. the
  // AI booking widget stuck on "Thinking…"). The backend AI parse itself caps
  // at ~22s, so 40s leaves margin for slow networks while still resolving.
  timeout: 40000,
});

api.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Surface auth errors clearly: on 401 we sign out so the user can re-login;
// on 403 we keep them logged in but log a clear message so the network panel
// makes sense ("you're logged in as the wrong role for this page").
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    // The privacy-reveal endpoints intentionally return 401 on an incorrect
    // PIN/password — that is a form-level error, NOT an expired auth token,
    // and must not trigger a global sign-out.
    const reqUrl = error?.config?.url || "";
    const isPrivacyUnlock = /\/privacy\/unlock(\?|$)/.test(reqUrl);
    if (status === 401 && !isPrivacyUnlock) {
      tokenStore.clear();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.assign("/login");
      }
    } else if (status === 403) {
      // eslint-disable-next-line no-console
      console.warn(
        "[auth] 403 Forbidden — current token's role doesn't match this endpoint. " +
          "Sign out and log in again as the correct role."
      );
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  // Structured error envelopes from FastAPI handlers — e.g. the geofence
  // guard returns {code, message, distance_m}. Prefer the human message.
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  // Last-resort serialisation that NEVER emits "[object Object]".
  try {
    return JSON.stringify(detail);
  } catch {
    return "Something went wrong";
  }
}
